/**
 * TaskDetail Component
 *
 * Displays a detailed view of a single task including:
 * - Task metadata (title, description, status, priority)
 * - Assignee information
 * - Labels
 * - Comments (linked messages)
 * - History/Audit trail
 *
 * @module components/tasks/TaskDetail
 */

import { memo, useState, useCallback, useEffect } from 'react';
import {
  X,
  Edit3,
  Trash2,
  Clock,
  Calendar,
  User,
  Bot,
  MessageCircle,
  History,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Save,
  XCircle,
  Loader2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import TaskStatusBadge, { TASK_STATUS_CONFIG } from './TaskStatusBadge.jsx';
import TaskPriorityBadge, { TASK_PRIORITY_CONFIG } from './TaskPriorityBadge.jsx';
import TaskLabelBadge from './TaskLabelBadge.jsx';
import RichTextEditor from '../common/RichTextEditor.jsx';
import { Avatar, AvatarWithLabel } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * Tab options for the detail view
 */
const TABS = {
  COMMENTS: 'comments',
  HISTORY: 'history'
};

// ============================================
// Helper Components
// ============================================

/**
 * Format a date for display
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format duration in minutes to human readable
 */
function formatDuration(minutes) {
  if (!minutes) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Assignee display component - wraps AvatarWithLabel
 */
const AssigneeDisplay = memo(function AssigneeDisplay({ assignee, type }) {
  if (!assignee) {
    return (
      <span className="text-gray-500 italic">Unassigned</span>
    );
  }

  return (
    <AvatarWithLabel
      name={assignee.displayName || assignee.name}
      avatarUrl={assignee.avatarUrl}
      type={type}
      size="md"
      showType={true}
    />
  );
});

/**
 * Metadata row component
 */
const MetadataRow = memo(function MetadataRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500 mb-0.5">{label}</div>
        <div className="text-sm text-gray-300">{children}</div>
      </div>
    </div>
  );
});

/**
 * Comment item component
 */
const CommentItem = memo(function CommentItem({ comment }) {
  const Icon = comment.senderType === 'agent' ? Bot : User;

  return (
    <div className="flex gap-3 py-3 border-b border-gray-700 last:border-0">
      <div className={`
        w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
        ${comment.senderType === 'agent' ? 'bg-indigo-600' : 'bg-emerald-600'}
      `}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-gray-200">
            {comment.senderName || 'Unknown'}
          </span>
          <span className="text-xs text-gray-500">
            {formatDate(comment.createdAt)}
          </span>
        </div>
        <div className="text-sm text-gray-400 whitespace-pre-wrap">
          {comment.content}
        </div>
      </div>
    </div>
  );
});

/**
 * History item component
 */
const HistoryItem = memo(function HistoryItem({ entry }) {
  const Icon = entry.actorType === 'agent' ? Bot : User;

  // Format the change description
  const getChangeDescription = () => {
    const field = entry.field;
    const oldVal = entry.oldValue;
    const newVal = entry.newValue;

    if (field === 'status') {
      return (
        <span className="flex items-center gap-1 flex-wrap">
          <span>Status changed from</span>
          <TaskStatusBadge status={oldVal} size="sm" showLabel={false} />
          <ArrowRight className="w-3 h-3 text-gray-500" />
          <TaskStatusBadge status={newVal} size="sm" showLabel={false} />
        </span>
      );
    }

    if (field === 'priority') {
      return (
        <span className="flex items-center gap-1 flex-wrap">
          <span>Priority changed from</span>
          <TaskPriorityBadge priority={oldVal} size="sm" />
          <ArrowRight className="w-3 h-3 text-gray-500" />
          <TaskPriorityBadge priority={newVal} size="sm" />
        </span>
      );
    }

    if (field === 'assignee') {
      if (!oldVal && newVal) {
        return `Assigned to ${newVal}`;
      }
      if (oldVal && !newVal) {
        return `Unassigned from ${oldVal}`;
      }
      return `Reassigned from ${oldVal} to ${newVal}`;
    }

    if (field === 'labels') {
      return `Labels updated`;
    }

    return `${field} changed from "${oldVal || '-'}" to "${newVal || '-'}"`;
  };

  return (
    <div className="flex gap-3 py-3 border-b border-gray-700 last:border-0">
      <div className={`
        w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
        ${entry.actorType === 'agent' ? 'bg-indigo-600/50' : 'bg-emerald-600/50'}
      `}>
        <Icon className="w-3 h-3 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-300 mb-1">
          {getChangeDescription()}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{entry.actorName || 'System'}</span>
          <span>•</span>
          <span>{formatDate(entry.createdAt)}</span>
        </div>
        {entry.reason && (
          <div className="text-xs text-gray-500 mt-1 italic">
            Reason: {entry.reason}
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Empty state component
 */
const EmptyState = memo(function EmptyState({ icon: Icon, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-gray-500">
      <Icon className="w-8 h-8 mb-2 opacity-50" />
      <span className="text-sm">{message}</span>
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * Inline editable status selector
 */
const StatusSelector = memo(function StatusSelector({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="cursor-pointer hover:opacity-80 transition-opacity"
      >
        <TaskStatusBadge status={value} size="md" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[140px]">
          {Object.keys(TASK_STATUS_CONFIG).map(status => (
            <button
              key={status}
              onClick={() => {
                onChange(status);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-1.5 text-left hover:bg-gray-700 transition-colors ${
                value === status ? 'bg-gray-700' : ''
              }`}
            >
              <TaskStatusBadge status={status} size="sm" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Inline editable priority selector
 */
const PrioritySelector = memo(function PrioritySelector({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="cursor-pointer hover:opacity-80 transition-opacity"
      >
        <TaskPriorityBadge priority={value} size="md" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[120px]">
          {Object.keys(TASK_PRIORITY_CONFIG).map(priority => (
            <button
              key={priority}
              onClick={() => {
                onChange(priority);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-1.5 text-left hover:bg-gray-700 transition-colors ${
                value === priority ? 'bg-gray-700' : ''
              }`}
            >
              <TaskPriorityBadge priority={priority} size="sm" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Inline editable assignee selector
 */
const AssigneeSelector = memo(function AssigneeSelector({ value, assignees = [], onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const currentAssignee = assignees.find(a => a.id === value);

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="cursor-pointer hover:opacity-80 transition-opacity text-left"
      >
        <AssigneeDisplay assignee={currentAssignee} type={currentAssignee?.type} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[200px] max-h-64 overflow-y-auto">
          <button
            onClick={() => {
              onChange(null);
              setIsOpen(false);
            }}
            className={`w-full px-3 py-2 text-left hover:bg-gray-700 transition-colors ${
              !value ? 'bg-gray-700' : ''
            }`}
          >
            <span className="text-gray-500 italic">Unassigned</span>
          </button>
          {assignees.map(assignee => (
            <button
              key={assignee.id}
              onClick={() => {
                onChange(assignee.id);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left hover:bg-gray-700 transition-colors ${
                value === assignee.id ? 'bg-gray-700' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <Avatar
                  name={assignee.displayName || assignee.name}
                  avatarUrl={assignee.avatarUrl}
                  type={assignee.type}
                  size="sm"
                />
                <span className="text-sm text-gray-200">{assignee.displayName || assignee.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Calculate actual minutes from startedAt to completedAt
 */
function calculateActualMinutes(startedAt, completedAt) {
  if (!startedAt || !completedAt) return null;
  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const diffMs = end - start;
  if (diffMs <= 0) return null;
  return Math.round(diffMs / (1000 * 60));
}

/**
 * Parse time input string to minutes
 * Supports formats: "90", "1h 30m", "1:30", "1h30m"
 */
function parseTimeInput(input) {
  if (!input || input.trim() === '') return null;
  const str = input.trim().toLowerCase();

  // Pure number = minutes
  if (/^\d+$/.test(str)) {
    return parseInt(str, 10);
  }

  // Format: "1h 30m" or "1h30m" or "2h" or "45m"
  const hMatch = str.match(/(\d+)\s*h/);
  const mMatch = str.match(/(\d+)\s*m/);
  if (hMatch || mMatch) {
    const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
    const mins = mMatch ? parseInt(mMatch[1], 10) : 0;
    return hours * 60 + mins;
  }

  // Format: "1:30"
  const colonMatch = str.match(/^(\d+):(\d+)$/);
  if (colonMatch) {
    return parseInt(colonMatch[1], 10) * 60 + parseInt(colonMatch[2], 10);
  }

  return null;
}

/**
 * Time input component for actualMinutes
 */
const TimeInput = memo(function TimeInput({ value, onChange, disabled, placeholder = '0m' }) {
  const [inputValue, setInputValue] = useState('');

  // Initialize input value from minutes
  useEffect(() => {
    if (value) {
      setInputValue(formatDuration(value));
    } else {
      setInputValue('');
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    const minutes = parseTimeInput(inputValue);
    onChange(minutes);
    if (minutes !== null) {
      setInputValue(formatDuration(minutes));
    }
  }, [inputValue, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }, []);

  return (
    <input
      type="text"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      className="w-20 px-2 py-0.5 text-sm bg-gray-800 border border-gray-700 rounded text-gray-200 focus:outline-none focus:border-indigo-500 disabled:opacity-50"
      title="Enter time (e.g., 90, 1h 30m, 1:30)"
    />
  );
});

/**
 * TaskDetail Component
 *
 * @param {object} props - Component props
 * @param {object} props.task - Task object
 * @param {array} [props.comments=[]] - Task comments
 * @param {array} [props.history=[]] - Task history entries
 * @param {array} [props.assignees=[]] - Available assignees for selection
 * @param {boolean} [props.isLoadingComments=false] - Loading state for comments
 * @param {boolean} [props.isLoadingHistory=false] - Loading state for history
 * @param {function} [props.onClose] - Close handler
 * @param {function} [props.onEdit] - Edit handler (deprecated - use onSave)
 * @param {function} [props.onSave] - Save handler for inline edits
 * @param {function} [props.onDelete] - Delete handler
 * @param {function} [props.onStatusChange] - Status change handler
 * @param {boolean} [props.isSaving=false] - Whether save is in progress
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Detail panel element
 */
const TaskDetail = memo(function TaskDetail({
  task,
  comments = [],
  history = [],
  assignees = [],
  isLoadingComments = false,
  isLoadingHistory = false,
  onClose,
  onEdit,
  onSave,
  onDelete,
  onStatusChange,
  isSaving = false,
  className = ''
}) {
  const [activeTab, setActiveTab] = useState(TABS.COMMENTS);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Initialize edit data when entering edit mode or task changes
  useEffect(() => {
    if (task) {
      setEditData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'backlog',
        priority: task.priority || 'medium',
        assigneeId: task.assigneeId || task.assignee?.id || null,
        actualMinutes: task.actualMinutes || null
      });
    }
  }, [task]);

  const toggleDescription = useCallback(() => {
    setIsDescriptionExpanded(prev => !prev);
  }, []);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setEditData({
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'backlog',
      priority: task.priority || 'medium',
      assigneeId: task.assigneeId || task.assignee?.id || null,
      actualMinutes: task.actualMinutes || null
    });
  }, [task]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditData({
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'backlog',
      priority: task.priority || 'medium',
      assigneeId: task.assigneeId || task.assignee?.id || null,
      actualMinutes: task.actualMinutes || null
    });
  }, [task]);

  const handleSave = useCallback(async () => {
    if (onSave) {
      const success = await onSave(task.id, editData);
      if (success !== false) {
        setIsEditing(false);
      }
    }
  }, [task, editData, onSave]);

  const updateEditField = useCallback((field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  }, []);

  if (!task) {
    return (
      <div className={`bg-gray-900 rounded-lg p-6 ${className}`}>
        <EmptyState icon={AlertCircle} message="No task selected" />
      </div>
    );
  }

  const {
    title,
    description,
    status,
    priority,
    labels = [],
    assignee: taskAssignee,
    assigneeId,
    assigneeType: taskAssigneeType,
    estimatedMinutes,
    actualMinutes,
    createdAt,
    startedAt,
    completedAt,
    project,
    feature
  } = task;

  // Fallback: If assignee is null but assigneeId exists, find from assignees list
  const fallbackAssignee = assigneeId && assignees?.find(a => a.id === assigneeId);
  const assignee = taskAssignee || fallbackAssignee || null;
  const assigneeType = taskAssigneeType || assignee?.type || null;

  const hasLongDescription = description && description.length > 200;

  return (
    <div className={`bg-gray-900 rounded-lg flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-gray-700">
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {isEditing ? (
              <>
                <StatusSelector
                  value={editData.status}
                  onChange={(v) => updateEditField('status', v)}
                  disabled={isSaving}
                />
                <PrioritySelector
                  value={editData.priority}
                  onChange={(v) => updateEditField('priority', v)}
                  disabled={isSaving}
                />
              </>
            ) : (
              <>
                <TaskStatusBadge status={status} size="md" />
                <TaskPriorityBadge priority={priority} size="md" />
              </>
            )}
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editData.title}
              onChange={(e) => updateEditField('title', e.target.value)}
              disabled={isSaving}
              className="w-full text-lg font-semibold text-gray-100 bg-gray-800 border border-gray-700 rounded px-2 py-1 focus:outline-none focus:border-indigo-500"
              placeholder="Task title..."
            />
          ) : (
            <h2 className="text-lg font-semibold text-gray-100 line-clamp-2">
              {title}
            </h2>
          )}
          {(project || feature) && (
            <div className="text-xs text-gray-500 mt-1">
              {project?.displayName || project?.name}
              {feature && ` / ${feature.name}`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                title="Save changes"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                title="Cancel"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              {(onEdit || onSave) && (
                <button
                  onClick={onSave ? handleStartEdit : () => onEdit(task)}
                  className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Edit task"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(task)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Delete task"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Description */}
        {isEditing ? (
          <div className="p-4 border-b border-gray-700">
            <div className="text-xs text-gray-500 mb-2">Description</div>
            <RichTextEditor
              value={editData.description}
              onChange={(v) => updateEditField('description', v)}
              placeholder="Enter task description..."
              disabled={isSaving}
              minHeight={150}
            />
          </div>
        ) : description ? (
          <div className="p-4 border-b border-gray-700">
            <div
              className={`prose prose-sm prose-invert max-w-none text-gray-400 ${
                !isDescriptionExpanded && hasLongDescription ? 'line-clamp-3' : ''
              }`}
            >
              <ReactMarkdown>{description}</ReactMarkdown>
            </div>
            {hasLongDescription && (
              <button
                onClick={toggleDescription}
                className="text-xs text-indigo-400 hover:text-indigo-300 mt-2 flex items-center gap-1"
              >
                {isDescriptionExpanded ? (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronRight className="w-3 h-3" />
                    Show more
                  </>
                )}
              </button>
            )}
          </div>
        ) : isEditing ? null : null}

        {/* Labels */}
        {labels.length > 0 && (
          <div className="p-4 border-b border-gray-700">
            <div className="text-xs text-gray-500 mb-2">Labels</div>
            <div className="flex flex-wrap gap-1.5">
              {labels.map(label => (
                <TaskLabelBadge
                  key={label.id || label.name}
                  label={label}
                  size="sm"
                />
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="p-4 border-b border-gray-700 space-y-1">
          <MetadataRow icon={User} label="Assignee">
            {isEditing ? (
              <AssigneeSelector
                value={editData.assigneeId}
                assignees={assignees}
                onChange={(v) => updateEditField('assigneeId', v)}
                disabled={isSaving}
              />
            ) : (
              <AssigneeDisplay assignee={assignee} type={assigneeType} />
            )}
          </MetadataRow>

          <MetadataRow icon={Clock} label="Time Tracking">
            <div className="flex items-center gap-4">
              <span>Est: {formatDuration(estimatedMinutes)}</span>
              <div className="flex items-center gap-1">
                <span>Actual:</span>
                {isEditing && (status === 'done' || status === 'review') ? (
                  <TimeInput
                    value={editData.actualMinutes}
                    onChange={(v) => updateEditField('actualMinutes', v)}
                    disabled={isSaving}
                    placeholder={
                      !actualMinutes && startedAt && completedAt
                        ? `~${formatDuration(calculateActualMinutes(startedAt, completedAt))}`
                        : '0m'
                    }
                  />
                ) : (
                  <span>
                    {actualMinutes
                      ? formatDuration(actualMinutes)
                      : (status === 'done' && startedAt && completedAt)
                        ? `~${formatDuration(calculateActualMinutes(startedAt, completedAt))}`
                        : '-'
                    }
                  </span>
                )}
              </div>
            </div>
          </MetadataRow>

          <MetadataRow icon={Calendar} label="Dates">
            <div className="space-y-1 text-xs">
              <div>Created: {formatDate(createdAt)}</div>
              {startedAt && <div>Started: {formatDate(startedAt)}</div>}
              {completedAt && <div>Completed: {formatDate(completedAt)}</div>}
            </div>
          </MetadataRow>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700">
          <div className="flex">
            <button
              onClick={() => setActiveTab(TABS.COMMENTS)}
              className={`
                flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2
                border-b-2 transition-colors
                ${activeTab === TABS.COMMENTS
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
                }
              `}
            >
              <MessageCircle className="w-4 h-4" />
              Comments
              {comments.length > 0 && (
                <span className="text-xs bg-gray-700 px-1.5 py-0.5 rounded">
                  {comments.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab(TABS.HISTORY)}
              className={`
                flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2
                border-b-2 transition-colors
                ${activeTab === TABS.HISTORY
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
                }
              `}
            >
              <History className="w-4 h-4" />
              History
              {history.length > 0 && (
                <span className="text-xs bg-gray-700 px-1.5 py-0.5 rounded">
                  {history.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === TABS.COMMENTS && (
            <div>
              {isLoadingComments ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full" />
                </div>
              ) : comments.length > 0 ? (
                <div className="divide-y divide-gray-700">
                  {comments.map((comment, index) => (
                    <CommentItem key={comment.id || index} comment={comment} />
                  ))}
                </div>
              ) : (
                <EmptyState icon={MessageCircle} message="No comments yet" />
              )}
            </div>
          )}

          {activeTab === TABS.HISTORY && (
            <div>
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full" />
                </div>
              ) : history.length > 0 ? (
                <div className="divide-y divide-gray-700">
                  {history.map((entry, index) => (
                    <HistoryItem key={entry.id || index} entry={entry} />
                  ))}
                </div>
              ) : (
                <EmptyState icon={History} message="No history yet" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default TaskDetail;
